import { useCallback, useRef, useState } from 'react';

import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter';

export const useEntriesState = () => {
    const [searchTexts, setSearchTexts] = useState<Record<number, string>>({});
    const [customOptions, setCustomOptions] = useState<
        Record<number, { label: string; value: number }[]>
    >({});
    const [selectedValues, setSelectedValues] = useState<Record<number, number>>({});
    const [fieldIds, setFieldIds] = useState<Record<number, number>>({});
    const [valueLabels, setValueLabels] = useState<Record<number, string>>({});
    const [requiredIds, setRequiredIds] = useState<number[]>([]);
    const [openStates, setOpenStates] = useState<Record<number, boolean>>({});

    const selectRefs = useRef<Record<number, React.RefObject<any>>>({});

    const getSelectRef = useCallback((fieldId: number) => {
        if (!selectRefs.current[fieldId]) {
            selectRefs.current[fieldId] = { current: null };
        }
        return selectRefs.current[fieldId];
    }, []);

    const handleValueChange = useCallback((fieldId: number, value: number) => {
        setSelectedValues((prev) => ({ ...prev, [fieldId]: value }));
    }, []);

    /**
     * Введённый в поле характеристики текст. Первая буква — заглавная: этим же
     * текстом заводится новое значение характеристики (см. `addCustomOption`),
     * то есть он попадает в справочник и дальше показывается всем. Приводим
     * здесь, в единственном месте, откуда берётся и подсказка, и новый вариант.
     */
    const handleSearch = useCallback(
        (fieldId: number, text: string) =>
            setSearchTexts((prev) => ({ ...prev, [fieldId]: capitalizeFirstLetter(text) })),
        []
    );

    const addCustomOption = useCallback(
        (fieldId: number, label: string) => {
            const newValue = Date.now();
            const newOpt = { label, value: newValue };

            setCustomOptions((prev) => ({
                ...prev,
                [fieldId]: [...(prev[fieldId] || []), newOpt],
            }));
            setValueLabels((prev) => ({ ...prev, [newValue]: label }));
            handleValueChange(fieldId, newValue);
            setSearchTexts((prev) => ({ ...prev, [fieldId]: '' }));

            setTimeout(() => selectRefs.current[fieldId]?.current?.blur?.());
        },
        [handleValueChange]
    );

    return {
        searchTexts,
        setSearchTexts,
        customOptions,
        setCustomOptions,
        selectedValues,
        setSelectedValues,
        fieldIds,
        setFieldIds,
        valueLabels,
        setValueLabels,
        requiredIds,
        setRequiredIds,
        openStates,
        setOpenStates,
        getSelectRef,
        handleValueChange,
        handleSearch,
        addCustomOption,
    };
};
